import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtTRY } from "@/lib/format";
import { SectionCard, StatCard, EmptyState } from "@/components/Common";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Info } from "lucide-react";

/**
 * KDV Raporu — aylık.  Veri kaynağı yalnızca faturalardır; tahsilat ve
 * ödeme kayıtlarının bu ekranla hiçbir bağı yoktur.  Backend hesabı:
 *   net = sale_vat − purchase_vat − carry_in
 *   net > 0 → tahmini ödenecek KDV
 *   net ≤ 0 → sonraki döneme devreden KDV
 */
const TR_MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

export default function VatReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/vat?year=${year}&month=${month}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  return (
    <div className="space-y-6" data-testid="vat-report">
      <SectionCard title="Dönem Seçimi">
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Yıl</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="vat-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Ay</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="vat-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TR_MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <div className="text-xs text-slate-500 leading-relaxed">
              Fatura tarihi seçilen aya düşen tüm satış ve alış faturaları rapor edilir.
              Tahsilat/ödeme tarihleri KDV hesabını etkilemez.
            </div>
          </div>
        </div>
      </SectionCard>

      {loading ? (
        <div className="p-8 text-center text-sm text-slate-500">Yükleniyor...</div>
      ) : !data || !data.has_invoices ? (
        <EmptyState
          icon={<Info className="w-8 h-8 text-slate-400" />}
          title="Bu dönemde fatura yok"
          description="Seçilen ay için satış veya alış faturası bulunmuyor."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              label="Hesaplanan KDV"
              value={fmtTRY(data.sale_vat)}
              accent="emerald"
              testId="vat-sale-vat"
            />
            <StatCard
              label="İndirilecek KDV"
              value={fmtTRY((data.purchase_vat ?? 0) + (data.expense_vat ?? 0))}
              accent="red"
              testId="vat-purchase-vat"
            />
            <StatCard
              label="Önceki Dönemden Devreden"
              value={fmtTRY(data.carry_in)}
              accent="slate"
              testId="vat-carry-in"
            />
            <StatCard
              label={data.tahmini_odenecek > 0 ? "Tahmini Ödenecek KDV" : "Sonraki Döneme Devreden"}
              value={fmtTRY(
                data.tahmini_odenecek > 0 ? data.tahmini_odenecek : data.sonraki_doneme_devreden,
              )}
              accent={data.tahmini_odenecek > 0 ? "amber" : "slate"}
              testId="vat-net"
            />
          </div>

          <SectionCard title={`${TR_MONTHS[month - 1]} ${year} — Detay`}>
            <div className="p-5 space-y-3 text-sm">
              <DetailRow label="Satışlardan Hesaplanan KDV" value={fmtTRY(data.sale_vat)} />
              <DetailRow label="Alış Faturalarından İndirilecek KDV" value={fmtTRY(data.purchase_vat)} negative />
              <DetailRow label="Faturalı Şirket Giderlerinden İndirilecek KDV" value={fmtTRY(data.expense_vat ?? 0)} negative testId="vat-expense-vat-row" />
              <DetailRow label="Önceki Dönemden Devreden KDV" value={fmtTRY(data.carry_in)} negative={data.carry_in > 0} />
              <div className="h-px bg-slate-200" />
              {data.tahmini_odenecek > 0 ? (
                <DetailRow label="Tahmini Ödenecek KDV" value={fmtTRY(data.tahmini_odenecek)} bold accent="amber" />
              ) : (
                <DetailRow label="Sonraki Döneme Devreden KDV" value={fmtTRY(data.sonraki_doneme_devreden)} bold />
              )}
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                Bu rapor Penz içerisinde bilgilendirme amaçlıdır.  Resmi KDV beyanı için
                muhasebecinizle doğrulama yapmanız önerilir.
              </p>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}

function DetailRow({ label, value, bold, negative, accent, testId }) {
  const textColor =
    accent === "amber" ? "text-amber-700" :
    negative ? "text-slate-600" : "text-slate-800";
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <span className={bold ? "font-semibold text-slate-900" : "text-slate-600"}>{label}</span>
      <span className={`${bold ? "font-semibold" : ""} ${textColor}`}>
        {negative ? "−" : ""}{value}
      </span>
    </div>
  );
}
